/**
 * Liquidity-crisis lab — domain model.
 *
 * Pure, deterministic functions with no UI or storage dependencies, so the
 * teaching math can be tested in isolation. All amounts are USD. This models a
 * single deposit run against Cedar River's fixed balance sheet (reused from the
 * bank lab) and shows how the run is met: cash first, then securities, then an
 * unmet shortfall once the liquid buffer is exhausted — all while economic
 * equity (solvency) is untouched.
 *
 * Deliberately simplified: no regulation, credit losses, taxes, or accrual
 * accounting. Securities sell at par by default; an optional fire-sale haircut
 * is the only thing that can dent equity, and it is off unless asked for.
 */

import { formatUsd, initialBank, type BankState } from '../bankLab/model'

export { formatUsd }

/** Slider granularity and range for the deposit run (USD). */
export const RUN_STEP = 1_000_000
export const MAX_RUN = 8_000_000

/** Fraction of par lost when securities are dumped in a fire sale (optional). */
export const HAIRCUT_RATE = 0.1

/** Cedar River — the same fixed starting model the bank lab uses. */
export function initialRunBank(): BankState {
  return initialBank()
}

/** Assets that can be turned into cash in time: cash + sellable securities. */
export function liquidBuffer(bank: BankState = initialRunBank()): number {
  return bank.cash + bank.securities
}

export interface RunAssessment {
  /** The deposit outflow demanded this week. */
  outflow: number
  /** Cash paid out first. */
  cashUsed: number
  /** Securities sold to cover the remainder of the outflow. */
  securitiesSold: number
  /** Outflow the bank cannot meet — loans can't be liquidated on demand. */
  shortfall: number
  /** Outflow actually met (outflow − shortfall). */
  met: number
  /** Liquid buffer still available after the run (0 once exhausted). */
  bufferRemaining: number
  /** Economic equity after the run — solvency. Unchanged unless a haircut applies. */
  equity: number
  /** True while the bank can hand over every dollar demanded. */
  canPay: boolean
  /** True while assets are still worth more than liabilities. */
  solvent: boolean
}

export interface RunOptions {
  /** Sell securities below par (fire sale), the only path that dents equity. */
  haircut?: boolean
}

/**
 * Assess a deposit run of `outflow` against the bank. Deterministic and pure:
 * the same inputs always produce the same assessment.
 */
export function assessRun(
  outflow: number,
  options: RunOptions = {},
  bank: BankState = initialRunBank(),
): RunAssessment {
  const demand = clampRun(outflow)

  const cashUsed = Math.min(demand, bank.cash)
  const securitiesSold = Math.min(Math.max(demand - bank.cash, 0), bank.securities)
  const shortfall = Math.max(demand - bank.cash - bank.securities, 0)
  const met = demand - shortfall
  const bufferRemaining = Math.max(liquidBuffer(bank) - demand, 0)

  // Paying out shrinks assets and liabilities together, so equity is untouched.
  // Only a fire-sale haircut on the securities actually sold reduces equity.
  const haircutLoss = options.haircut ? securitiesSold * HAIRCUT_RATE : 0
  const equity = bank.equity - haircutLoss

  return {
    outflow: demand,
    cashUsed,
    securitiesSold,
    shortfall,
    met,
    bufferRemaining,
    equity,
    canPay: shortfall === 0,
    solvent: equity > 0,
  }
}

/** Clamp a raw slider value to the allowed run range. */
export function clampRun(outflow: number): number {
  if (!Number.isFinite(outflow)) return 0
  return Math.max(0, Math.min(MAX_RUN, outflow))
}

/**
 * The learning goal: drive the run past the liquid buffer so the bank fails to
 * pay while its equity stays positive. Complete once a shortfall appears.
 */
export function isRunComplete(outflow: number, bank: BankState = initialRunBank()): boolean {
  return clampRun(outflow) > liquidBuffer(bank)
}

/** Plain-language read of the current run, for immediate, explanatory feedback. */
export function explainRun(assessment: RunAssessment): string {
  const { outflow, cashUsed, securitiesSold, shortfall, bufferRemaining } = assessment

  if (outflow === 0) {
    return 'No outflow yet. Cedar River is calm: a $5M liquid buffer sits ready and equity is +$2M.'
  }

  if (shortfall === 0) {
    return `Cedar River meets the ${formatUsd(outflow)} run — ${formatUsd(
      cashUsed,
    )} from cash and ${formatUsd(securitiesSold)} from selling securities — with ${formatUsd(
      bufferRemaining,
    )} of buffer to spare. Still liquid, still solvent.`
  }

  return `The run of ${formatUsd(outflow)} exhausts the ${formatUsd(
    liquidBuffer(),
  )} liquid buffer. Cedar River cannot raise the last ${formatUsd(
    shortfall,
  )}: its loans can't be sold in time. It FAILS on liquidity — yet equity is still ${formatUsd(
    assessment.equity,
  )}. Solvent, but illiquid.`
}
